import { Divider, Grid2, Typography } from "@mui/material";
import ExpandableTypography from "./ExpandableTypography";
import { allergies, behaviourNoteMapping } from "../enum/formEnum";

const ServiceDetail = ({ formData, fromReview = false }) => {
  const serviceLabels = {
    bathing: "Bathing",
    eating: "Eating",
    assistanceEatingNotes: "Assistance Eating Notes",
    toileting: "Toileting",
    mealPreparation: "Meal Preparation",
    dressingGrooming: "Dressing & Grooming",
    housework: "Light Housekeeping",
    companionship: "Companionship",
    transportationNeeded: "Transportation Needed",
    vehiclePreference: "Vehicle Preference",
    other: "Other",
    specifyOther: "Specify Other",
  };

  const additionalInformationLabels = {
    petsPresent: "Pets Present",
    petDetails: "Pet Detail",
    smokingEnvironment: "Smoking Environment",
    longTermCareInsurance: "Long-Term Care Insurance",
    policyNumber: "Policy Number",
    ltcInsuranceProvider: "Insurance Provider",
    ltcInsuranceAcknowledgment: "LTC Policy Acknowledgment",
    firearmsPresent: "Firearms Present",
    firearmsSecure: "Are the firearms stored securely?",
    allergies: "Allergies",
    allergiesNote: "Allergies Note",
    learnMore: "Learn More about LTC Insurance",
  };

  const clientCareNeedLabel = {
    medicalDiagnosis: "Medical Diagnosis, recent hospitalizations, or injuries",
    fallRisk: "Fall Risk",
    continence: "Continence",
    vision: "Vision",
    hearing: "Hearing",
    speech: "Speech",
    oxygenDependent: "Oxygen Dependent",
    isShoweringAssistance: "Is showering assistance required?",
    isShoweringAssistanceLevel: "What level of showering assistance is required?",
    isMedicationReminders: "Are medication reminders required?",
    isMedicationRemindersResponse: "Who is currently responsible?",
    isOralHygieneAssistance: "Is oral hygiene assistance required?",
    walker: "Uses a walker or cane",
    isMealtimeAssistance: "Is mealtime assistance required?",
    isMealtimeAssistanceLevel: "What level of mealtime assistance is required?",
    swallowing: "Swallowing",
    swallowingOther: "Please specify other",
    dental: "Dental",
  };

  const behaviorLabels = {
    confusion: "Confusion",
    confusionNotes: "Confusion Notes",
    agitation: "Agitation",
    agitationNotes: "Agitation Notes",
    suspicious: "Suspicious",
    suspiciousNotes: "Suspicious Notes",
    upAtNight: "Up at Night",
    upAtNightNotes: "Up at Night Notes",
    withdrawn: "Withdrawn",
    withdrawnNotes: "Withdrawn Notes",
    wanders: "Wanders",
    wandersNotes: "Wanders Notes",
    combative: "Combative",
    combativeNotes: "Combative Notes",
  };

  const mobilityDeviceEnumMap = {
    none: "None",
    wheelchair: "Wheelchair",
    walker: "Walker",
    cane: "Cane",
    crutches: "Crutches",
    guideDog: "Guide dog",
    both: "Both Walker & Cane",
  };

  const vehicleEnumMap = {
    caregiver: "Caregiver's Vehicle",
    client: "Client's Vehicle",
    either: "Either Vehicle",
    none: "No Vehicle Required",
  };
  const shouldShowAdditionalField = (key, info) => {
    const ltcFields = ["policyNumber", "ltcInsuranceProvider", "ltcInsuranceAcknowledgment"];

    if (key === "petDetails") return info?.petsPresent === true;
    if (ltcFields.includes(key)) return info?.longTermCareInsurance === true;
    if (key === "firearmsSecure") return info?.firearmsPresent === true;
    if (key === "learnMore") return info?.learnMore === true;

    return true;
  };
  const capitalizeFirst = (str) =>
    typeof str === "string" && str.length > 0
      ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
      : str;
  return (
    <>
      <Typography variant="h4" color="default.main">
        Individualized Service Plan
      </Typography>
      <Grid2 container spacing={3} mt={3}>
        {Object.entries(serviceLabels).map(([key, label]) => {
          const serviceData = formData?.variableFlexibleServices;
          const value = serviceData?.[key];

          // Conditional rendering logic
          switch (key) {
            case "vehiclePreference":
              if (!serviceData?.transportationNeeded) return null;
              break;
            case "assistanceEatingNotes":
              if (!serviceData?.eating) return null;
              break;
            case "specifyOther":
              if (!serviceData?.other) return null;
              break;
            default:
              break;
          }

          // Determine display value
          let displayValue;
          switch (key) {
            case "vehiclePreference":
              displayValue = vehicleEnumMap[value] || value;
              break;
            default:
              if (!value) {
                displayValue = "Not Required";
              } else if (value === true) {
                displayValue = "Required";
              } else {
                displayValue = value;
              }
              break;
          }

          return (
            <Grid2 size={{ xs: 12, sm: 4 }} key={key}>
              <Typography variant="body2" color="default.main" mb={0.5}>
                {label}
              </Typography>
              <ExpandableTypography>{displayValue}</ExpandableTypography>
            </Grid2>
          );
        })}
      </Grid2>

      {/* Client Care Needs */}

      <Typography variant="h4" color="default.main" mt={4.5}>
        Client Care Needs
      </Typography>

      <Grid2 container spacing={3} mt={3}>
        {Object.entries(clientCareNeedLabel)
          .filter(([key]) => {
            const care = formData?.clientCareNeeds;
            // Conditional display logic
            if (key === "isMealtimeAssistanceLevel" && care?.isMealtimeAssistance !== "Yes") {
              return false;
            }
            if (key === "isShoweringAssistanceLevel" && care?.isShoweringAssistance !== "Yes") {
              return false;
            }

            if (key === "isMedicationRemindersResponse" && care?.isMedicationReminders !== "Yes") {
              return false;
            }

            if (key === "swallowingOther" && care?.swallowing !== "Other") {
              return false;
            }

            return true;
          })
          .map(([key, label]) => {
            const value = formData?.clientCareNeeds?.[key];

            let displayValue = "N/A";

            if (Array.isArray(value)) {
              displayValue = value.length > 0
                ? value.map(capitalizeFirst).join(", ")
                : "N/A";
            } else if (typeof value === "string") {
              displayValue = value.trim() !== "" ? capitalizeFirst(value) : "N/A";
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }

            return (
              <Grid2 size={{ xs: 12, sm: 4 }} key={key}>
                <Typography variant="body2" color="default.main" mb={0.5}>
                  {label}
                </Typography>

                <ExpandableTypography chunkSize={100}>
                  {displayValue}
                </ExpandableTypography>
              </Grid2>
            );
          })}
      </Grid2>




      {/* Mobility & Assistance */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" color="default.main">
        Mobility & Assistance
      </Typography>
      <Grid2 container spacing={3} mt={3}>
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="default.main" mb={0.5}>
            Uses Wheelchair
          </Typography>
          <Typography variant="body1" color="default.light">
            {formData?.mobilityAssistanceHeading?.usesWheelchair ? "Yes" : "No"}
          </Typography>
        </Grid2>

        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="default.main" mb={0.5}>
            Mobility Assistance
          </Typography>
          <Typography variant="body1" color="default.light">
            {mobilityDeviceEnumMap[
              formData?.mobilityAssistanceHeading?.mobilityAssistance
            ]}
          </Typography>
        </Grid2>
      </Grid2>

      {/* Behavior & Cognition */}
      <Typography variant="h4" color="default.main" mt={4.5}>
        Behavior & Cognition
      </Typography>

      <Grid2 container spacing={3} mt={3}>
        {Object.entries(behaviorLabels).map(([key, label]) => {
          let value = formData?.behaviorCognition?.[key];
          const isNotesField = key.endsWith("Notes");
          if (isNotesField) {
            const noteMapping = behaviourNoteMapping[key]
            if (noteMapping && !value && formData?.behaviorCognition?.[noteMapping] === true) {
              value = "-"
            }
          }
          // Skip notes fields if empty
          if (isNotesField && (!value || typeof value !== "string" || value.trim() === "")) {
            return null;
          }

          // Determine display value
          let displayValue = "";

          if (isNotesField) {
            displayValue = value.trim();
          } else {
            const booleanValue = value === undefined ? false : value;
            displayValue = booleanValue ? "Required" : "Not Required";
          }

          return (
            <Grid2 size={{ xs: 12, sm: 4 }} key={key}>
              <Typography variant="body2" color="default.main" mb={0.5}>
                {label}
              </Typography>
              <ExpandableTypography chunkSize={100}>
                {displayValue}
              </ExpandableTypography>
            </Grid2>
          );
        })}
      </Grid2>

      <Typography variant="h4" color="default.main" mt={4.5}>
        Additional Information
      </Typography>

      <Grid2 container spacing={3} mt={3}>
        {Object.entries(additionalInformationLabels)
          .filter(([key]) => {
            // Special rule: only show allergiesNote if allergies has something
            if (key === "allergiesNote") {
              const allergies = formData?.additionalInformation?.allergies;
              return Array.isArray(allergies) && allergies.length > 0;
            }
            return shouldShowAdditionalField(key, formData?.additionalInformation);
          })
          .flatMap(([key, label]) => {
            const value = formData?.additionalInformation?.[key];

            // Render multiple pet detail entries
            if (key === "petDetails" && Array.isArray(value)) {
              return value.map((pet, index) => (
                <Grid2 size={{ xs: 12, sm: 4 }} key={`pet-${index}`}>
                  <Typography variant="body2" color="default.main" mb={0.5}>
                    {`Pet Details ${index + 1}`}
                  </Typography>
                  <Typography variant="body1" color="default.light">
                    {`Type: ${pet.petType ?? '-'}, Weight: ${pet.petWeight ?? '-'} lbs, Size: ${pet.size ?? '-'}`}
                  </Typography>
                </Grid2>
              ));
            }

            // Format value display
            let displayValue = "No";
            if (value === true) {
              displayValue = "Yes";
            } else if (typeof value === "string" && value.trim()) {
              displayValue = value;
            } else if (key === "allergies") {
              displayValue = Array.isArray(value) && value.length > 0 ? value.join(", ") : "No";
            } else if (key === "allergiesNote" && !value) {
              displayValue = "-";
            }

            return (
              <Grid2 size={{ xs: 12, sm: 4 }} key={key}>
                <Typography variant="body2" color="default.main" mb={0.5}>
                  {label}
                </Typography>
                <ExpandableTypography chunkSize={100}>
                  {displayValue}
                </ExpandableTypography>
              </Grid2>
            );
          })}
      </Grid2>


    </>
  );
};
export default ServiceDetail;